import VideoComponent from './Video';
import Play from './Play';
import Views from './Views';
import Likes from './Likes';
import { useRef, useState } from 'react';
import { __ } from '@wordpress/i18n';
import SbUtils from '../../../../Utils/SbUtils';
import { useGdpr } from '../../../../Frontend/Hooks/GdprContext';

const getThumbnailStyle = (thumbnailSize, postStyle, layout, isPlaceholder) => ({
    position: 'relative',
    width: thumbnailSize === 'large' ? '385px' : '100%',
    height: ['large', 'portrait'].includes(thumbnailSize) || layout === 'masonry' ? '100%' : '269px',
    overflow: 'hidden',
    borderRadius: postStyle === "regular" ? '8px' : '0px',
    background: isPlaceholder ? '#E8E8E8' : 'black',
    margin: 'auto',
});

/**
 * Get the image src respecting GDPR consent state.
 *
 * - Local images are always safe (hosted on your server).
 * - Remote images (cover_image_url) are only used when consent is given or GDPR is not active.
 * - Falls back to placeholder when GDPR is active and no consent.
 */
const getImageSrc = (post, gdprActive, gdprConsented, gdprPlaceholder) => {
    // Local images are always GDPR-safe.
    if (post?.local_cover_image_url) {
        return post.local_cover_image_url;
    }

    // If GDPR is not active or consent was given, use the remote image.
    if (!gdprActive || gdprConsented) {
        return post?.cover_image_url;
    }

    // GDPR active, no consent, no local image: use placeholder.
    // Never fall back to CDN image — that would defeat GDPR protection.
    return gdprPlaceholder || '';
};

const PostImage = ({
    isPro,
    post,
    feedSettings,
    activeIndex,
    setActiveIndex,
    postIndex,
    isPlaying,
    setIsPlaying,
    layout,
    handlePostClick,
    isFeedEditor,
}) => {
    const { gdprActive, gdprConsented, gdprPlaceholder } = useGdpr();
    const thumbnailSize = layout === 'list' ? 'large' : (feedSettings?.feedTemplate === 'widget' ? 'portrait' : 'medium');
    const postStyle = feedSettings?.postStyle || 'regular';
    const playerExperience = (isPro && feedSettings?.videoPlayer) || 'lightbox';
    const noMetaClass = (isPro && (feedSettings?.postElements?.includes('views') || feedSettings?.postElements?.includes('likes'))) ? '' : 'sb-no-meta';

    const [dynamicHeight, setDynamicHeight] = useState('inherit');
    const imageWrapRef = useRef(null);

    // Block inline video when GDPR is active and no consent.
    const canPlayVideo = !gdprActive || gdprConsented;

    const handleImageClick = () => {
        if (!isFeedEditor) {
            if (playerExperience === 'lightbox' || layout === 'gallery') {
                // Pass the triggering button node up so the layout can
                // restore focus to it when the lightbox closes (WCAG 2.4.3).
                // The lightbox is conditionally mounted and opened by this
                // plain <button>, not a Radix DialogTrigger, so Radix's
                // FocusScope has no trigger ref to restore to. SMASH-1377 v3-r11.
                handlePostClick(postIndex, imageWrapRef.current);
            } else if (canPlayVideo) {
                setIsPlaying(true);
                setActiveIndex(postIndex);
            } else if (post?.share_url) {
                // GDPR blocks inline playback — open the post on TikTok instead.
                window.open(post.share_url, '_blank', 'noopener,noreferrer');
            }
            if (imageWrapRef.current) {
                const height = imageWrapRef.current.clientHeight;
                setDynamicHeight(`${height}px`);
            }
        }
    };

    const imageSrc = getImageSrc(post, gdprActive, gdprConsented, gdprPlaceholder);
    const isPlaceholder = gdprActive && !gdprConsented && !post?.local_cover_image_url;

    // Closes finding v3 S1: the thumbnail was a clickable <div> with no
    // role or keyboard handler. The click handler can open the lightbox,
    // start inline playback, or open the post on TikTok depending on
    // settings/GDPR — i.e. it is a generic activation. Convert the wrap
    // to a real <button> so keyboard, focus, and AT semantics come along
    // for free. `sb-button-reset` strips default button chrome so the
    // existing thumbnail styling is preserved.
    // Use the caption as the button name only when it carries real text;
    // an emoji-only caption ("✨") would otherwise be the entire accessible
    // name (meaningless to AT — WCAG 2.4.4 / 4.1.2). SMASH-1382 v3 frontend.
    const ariaLabel = SbUtils.hasMeaningfulText(post?.video_description)
        ? post.video_description
        : __('Open post', 'sb-customizer');

    // a11y(SMASH-1377 F5): the inline player previously rendered the
    // <video controls> INSIDE the still-mounted thumbnail <button> — an ARIA
    // nested-interactive violation (WCAG 4.1.2): AT exposed one button named
    // by the caption and Enter/Space re-triggered the button instead of the
    // video controls. Render the player as a REPLACEMENT for the button (same
    // wrapper class + style preserve layout); VideoComponent moves focus to
    // the video on inline mount (F4).
    const isInlinePlaying = isPlaying && isPro && post?.video_url && canPlayVideo
        && activeIndex === postIndex && layout !== 'gallery';

    if (isInlinePlaying) {
        return (
            <div
                className='sb-post-item-image-wrap'
                style={getThumbnailStyle(thumbnailSize, postStyle, layout, isPlaceholder)}
            >
                <VideoComponent
                    post={post}
                    playerExperience={playerExperience}
                    dynamicHeight={dynamicHeight}
                />
            </div>
        );
    }

    return (
        <button
            type="button"
            ref={imageWrapRef}
            className='sb-post-item-image-wrap sb-button-reset'
            style={getThumbnailStyle(thumbnailSize, postStyle, layout, isPlaceholder)}
            onClick={handleImageClick}
            aria-label={ariaLabel}
        >

            <img
                className='sb-post-item-image'
                src={imageSrc}
                alt={SbUtils.generateAltText(post)}
            />

            {(layout === 'gallery' || ((!isPlaying || activeIndex !== postIndex) && layout !== 'list')) && (
                <>
                    {feedSettings?.postElements?.includes('playIcon') &&
                        <Play className={noMetaClass} />
                    }

                    <div className='sb-post-item-meta'>
                        {isPro && feedSettings?.postElements?.includes('views') &&
                            <Views post={post} />
                        }

                        {isPro && feedSettings?.postElements?.includes('likes') &&
                            <Likes post={post} />
                        }
                    </div>
                </>
            )}
        </button>
    );
}

export default PostImage;
